/**
 * Form field normalisation — mirrors the standardizeFormFields logic in gfs-script.js.
 *
 * Rules (applied on blur / before submit):
 *   name  — title-case (hyphen-aware); error if looks like email or has invalid chars
 *   email — lowercase; error if not valid email format
 *   phone — strip all non-digit characters
 *   city  — title-case
 *   zip   — strip non-digit/hyphen; 9-digit string → XXXXX-XXXX
 *   other single-line — collapse multiple spaces, trim
 */

/** Letters, digits, spaces, hyphens, apostrophes, periods. Matches gfs-script.js. */
const INVALID_NAME_CHARS = /[^a-zA-Z0-9\s'\-.]/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function looksLikeEmail(str: string): boolean {
  return EMAIL_REGEX.test(str);
}

export function validateEmail(str: string): string | null {
  if (!str) return null;
  if (!EMAIL_REGEX.test(str.toLowerCase())) return "Please enter a valid email address (e.g. name@example.com).";
  return null;
}

export function hasInvalidNameChars(str: string): boolean {
  return INVALID_NAME_CHARS.test(str);
}

export function titleCase(str: string): string {
  return str
    .replace(/\s+/g, " ")
    .split(" ")
    .map((word) =>
      word
        .split("-")
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
        .join("-"),
    )
    .join(" ");
}

// Auto-capitalises names only when the user clearly didn't intend a specific casing.
// Mixed-case input (McDonald, Dell'Arena, deVito) is left exactly as typed.
function normaliseNameCasing(str: string): string {
  const collapsed = str.replace(/\s+/g, " ");
  if (collapsed !== collapsed.toLowerCase() && collapsed !== collapsed.toUpperCase()) {
    return collapsed;
  }
  return collapsed
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(" ");
}

export function normalisePhone(str: string): string {
  const digits = str.replace(/\D/g, "");
  // Strip US country code prefix — Altru stores numbers as 11-digit (+1xxxxxxxxxx)
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
}

export function formatPhone(digits: string): string {
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return digits;
}

export function validatePhone(digits: string): string | null {
  if (digits.length === 0) return null;
  if (digits.length !== 10) return "Phone number must contain exactly 10 digits.";
  return null;
}

export function normaliseZip(str: string): string {
  const clean = str.replace(/[^\d-]/g, "").trim();
  // Truncate ZIP+4 to 5 digits
  if (/^\d{9}$/.test(clean) || /^\d{5}-\d{4}$/.test(clean)) return clean.slice(0, 5);
  return clean;
}

export function validateZip(zip: string): string | null {
  if (!zip) return null;
  if (/^\d{5}$/.test(zip)) return null;
  return "Please enter a valid 5-digit ZIP code.";
}

export type FieldType = "name" | "email" | "phone" | "city" | "zip" | "other";

/** Map a field name / element ID to a normalisation category. */
export function getFieldType(name: string): FieldType {
  // Collapse hyphens so "profile-first-name" → "profilefirstname"
  const n = name.toLowerCase().replace(/-/g, "");
  if (n.includes("firstname") || n.includes("lastname")) return "name";
  if (n.includes("email")) return "email";
  if (n.includes("phone")) return "phone";
  if (n.includes("city")) return "city";
  if (n.includes("zip") || n.includes("postcode") || n.includes("postalcode")) return "zip";
  return "other";
}

export interface NormaliseResult {
  value: string;
  error: string | null;
}

/**
 * Normalise a single field value on blur or submit.
 * Returns the normalised value and an optional inline error message.
 */
export function normaliseField(fieldType: FieldType, raw: string): NormaliseResult {
  const trimmed = raw.trim();

  if (!trimmed) return { value: raw, error: null };

  switch (fieldType) {
    case "name": {
      if (looksLikeEmail(trimmed)) {
        return { value: raw, error: "Please enter your name, not your email address." };
      }
      if (hasInvalidNameChars(trimmed)) {
        return { value: raw, error: "Name may only contain letters, hyphens, apostrophes, or periods." };
      }
      return { value: normaliseNameCasing(trimmed), error: null };
    }
    case "email": {
      const lower = trimmed.toLowerCase();
      if (!looksLikeEmail(lower)) {
        return { value: raw, error: "Please enter a valid email address (e.g. name@example.com)." };
      }
      return { value: lower, error: null };
    }
    case "phone": {
      const digits = normalisePhone(raw);
      return { value: digits, error: validatePhone(digits) };
    }
    case "city":
      return { value: titleCase(trimmed), error: null };
    case "zip": {
      const zip = normaliseZip(raw);
      return { value: zip, error: validateZip(zip) };
    }
    default: {
      const collapsed = trimmed.replace(/\s+/g, " ");
      // Mirror gfs-script.js textarea rule: auto title-case if value is ALL CAPS or all lowercase
      const upper = collapsed.toUpperCase();
      const lower = collapsed.toLowerCase();
      if (collapsed === upper || collapsed === lower) {
        return { value: titleCase(collapsed), error: null };
      }
      return { value: collapsed, error: null };
    }
  }
}
